const API_KEYS = {
};

// D1 数据库配置
const D1_API_BASE = 'https://worker-mute-bar-e9b8.mr-lightson.workers.dev';
const CACHE_DURATION = 12 * 60 * 60 * 1000; // 12小时

// 从 D1 获取缓存的新闻数据
const getCachedNewsFromD1 = async () => {
  try {
    const response = await fetch(`${D1_API_BASE}/cache/news`);
    
    if (response.ok) {
      const data = await response.json();
      console.log('📦 从 D1 缓存获取新闻数据');
      return data;
    } else {
      console.error('❌ D1 获取失败，状态码:', response.status);
      return null;
    }
  } catch (error) {
    console.error('❌ 从 D1 获取缓存数据失败:', error);
    return null;
  }
};

// 保存新闻数据到 D1
const saveNewsToD1 = async (newsData) => {
  try {
    const response = await fetch(`${D1_API_BASE}/cache/news`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        news: newsData,
        timestamp: Date.now()
      })
    });
    
    if (response.ok) {
      console.log('💾 新闻数据已保存到 D1 数据库');
      return true;
    } else {
      console.error('❌ 保存到 D1 失败，状态码:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ 保存数据到 D1 失败:', error);
    return false;
  }
};

// 请求配置表
const newsSources = {
  cryptopanic: {
    buildUrl: ({ page }) =>
      `https://cryptopanic.com/api/developer/v2/posts/?auth_token=${API_KEYS.cryptopanic}&public=true&page=${page}`,
    extractData: (res) => res.results || [],
  },

  newsapi: {
    buildUrl: ({ page, query }) =>
      `https://worker-mute-bar-e9b8.mr-lightson.workers.dev/?q=${encodeURIComponent(query || 'crypto')}&page=${page}`,    
    extractData: (res) => res.articles || [],
  },
};

/**
 * 通用新闻抓取器 - 使用 D1 数据库缓存
 * @param {Object} options 配置项
 * @param {'cryptopanic' | 'newsapi'} options.source 来源
 * @param {number} [options.page=1] 页码
 * @param {string} [options.query] 查询关键词（仅适用于 NewsAPI）
 * @param {boolean} [options.forceRefresh=false] 强制刷新，跳过缓存
 */
export const fetchNews = async ({ source = 'newsapi', page = 1, query = 'crypto', forceRefresh = false } = {}) => {
  // 首先尝试从 D1 缓存获取数据
  if (!forceRefresh && page === 1) {
    const cachedData = await getCachedNewsFromD1();
    if (cachedData && cachedData.news && cachedData.timestamp) {
      const cacheAge = Date.now() - cachedData.timestamp;
      if (cacheAge < CACHE_DURATION) {
        console.log(`📦 使用 D1 缓存数据 (缓存时间: ${Math.round(cacheAge / (1000 * 60))} 分钟前)`);
        return cachedData.news;
      } else {
        console.log('⏰ D1 缓存已过期，重新获取数据');
      }
    }
  }

  // 如果没有缓存或缓存过期，从 API 获取数据
  const config = newsSources[source];

  if (!config) {
    throw new Error(`Unsupported news source: ${source}`);
  }

  const url = config.buildUrl({ page, query });

  try {
    console.log('🌐 从 NewsAPI 获取新数据...');
    const response = await fetch(url);
    const data = await response.json();
    console.log('Raw data:', data);
    const extracted = config.extractData(data);
    const normalizedNews = normalizeNews(extracted, source);
    
    // 按照发布时间排序，最新的在前面
    const sortedNews = normalizedNews.sort((a, b) => {
      const dateA = new Date(a.publishedAt);
      const dateB = new Date(b.publishedAt);
      return dateB - dateA; // 降序排列，最新的在前
    });

    // 如果是第一页，保存到 D1 数据库
    if (page === 1 && sortedNews.length > 0) {
      await saveNewsToD1(sortedNews);
    }

    return sortedNews;
  } catch (err) {
    console.error(`Failed to fetch news from ${source}:`, err);
    
    // 如果 API 请求失败，尝试返回 D1 缓存数据（即使过期）
    const fallbackData = await getCachedNewsFromD1();
    if (fallbackData && fallbackData.news) {
      console.log('⚠️ API 请求失败，使用 D1 缓存数据作为备选');
      return fallbackData.news;
    }
    
    return [];
  }
};

// 手动刷新缓存
export const refreshNewsCache = async () => {
  console.log('🔄 手动刷新新闻缓存...');
  return await fetchNews({ forceRefresh: true });
};

// 获取缓存状态信息
export const getCacheStatus = async () => {
  try {
    const cachedData = await getCachedNewsFromD1();
    if (cachedData && cachedData.timestamp) {
      const cacheAge = Date.now() - cachedData.timestamp;
      const hoursOld = Math.round(cacheAge / (1000 * 60 * 60) * 10) / 10;
      const isExpired = cacheAge > CACHE_DURATION;
      
      return {
        hasCachedData: true,
        cacheAge: cacheAge,
        hoursOld: hoursOld,
        isExpired: isExpired,
        newsCount: cachedData.news?.length || 0,
        lastUpdate: new Date(cachedData.timestamp).toLocaleString('zh-CN')
      };
    }
    return { hasCachedData: false };
  } catch (error) {
    console.error('获取缓存状态失败:', error);
    return { hasCachedData: false, error: error.message };
  }
};

const normalizeNews = (items, source) => {
  return items.map((item) => {
    if (source === 'cryptopanic') {
      return {
        source: item.domain || 'Unknown Source',
        publishedAt: item.published_at,
        title: item.title,
        url: item.url,
        content: item.body,
        description: item.description,
        image: item.image,
      };
    }

    if (source === 'newsapi') {
      return {
        source: item.source?.name || 'Unknown Source',
        publishedAt: item.publishedAt,
        title: item.title,
        url: item.url,
        content: item.content,
        description: item.description,
        image: item.urlToImage,
      };
    }

    return null;
  }).filter(Boolean);
};
